import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { getLocalStorageItem, popup } from '../../../utils';
import {
    LOCAL_STORAGE,
    MY_DOCUMENTS,
    MY_DOCUMENTS_COLUMNS,
    QUERY_KEYS,
    ROUTES,
} from '../../../constants';
import { AdminPageHeader } from '../../Admin';
import { usePagination } from '../../../hooks';
import { getAllMyDocumentsAPI } from '../../../utils/apiServices/myDocumentsAPI';
import { Table } from '../../shared';
import UploadMyDocumentModal from './UploadMyDocumentModal';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const DocumentsView = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const folderRef = useRef(undefined);
    const folderId = location?.state?.folderId ?? folderRef.current?.folderId;
    const folderName = location?.state?.folderName ?? folderRef.current?.folderName;
    const accountId = location?.state?.accountId ?? folderRef.current?.accountId;
    const currentAccountId = getLocalStorageItem(LOCAL_STORAGE.CUSTOMER_ID);
    const [response, setResponse] = useState({
        data: [],
        count: 1,
    });
    const pagination = usePagination(response.data.length, response.count);
    const [search, setSearch] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);

    const [debounceSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [QUERY_KEYS.MY_DOCUMENTS,accountId, folderId, debounceSearch, pagination.limit, pagination.offset],
        queryFn: async () =>
            await getAllMyDocumentsAPI({
                account_id: accountId,
                folder_id: folderId,
                limit: pagination.limit,
                offset: pagination.offset,
                search: debounceSearch,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
        enabled: !!accountId && !!folderId,
    });

    useEffect(() => {
        if (data.data && isFetched) {
            setResponse({ data: data.data, count: data.count });
        }
    }, [data, isFetched]);

    if (isError) popup('error', error.message);

    useEffect(() => {
        if (location.state?.folderId) {
            folderRef.current = {
                folderId: location.state.folderId,
                accountId: location.state.accountId,
                folderName: location.state.folderName,
            };
        }
    }, [location.state]);

    useEffect(() => {
        if (currentAccountId && accountId && currentAccountId !== accountId) {
            navigate(ROUTES.CUSTOMER.MY_FOLDERS_VIEW);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentAccountId, accountId]);

    return (
        <>
            <AdminPageHeader
                showAddButton
                heading={folderName}
                buttonText={MY_DOCUMENTS.CREATE_NEW}
                handleAddClick={() => {
                    setShowAddModal(true);
                }}
                backbutton
                showSearchBar
                searchBarPlaceholder={MY_DOCUMENTS.FILE_NAME_SEARCH}
                count={pagination.dataCount}
                setSearch={setSearch}
                AddModal={
                    showAddModal ? (
                        <UploadMyDocumentModal
                            showModal={showAddModal}
                            setShowModal={setShowAddModal}
                            folderId={folderId}
                            folderName={folderName}
                        />
                    ) : null
                }
            />
            <Table
                columns={MY_DOCUMENTS_COLUMNS}
                data={response.data}
                pagination={pagination}
                pageName={MY_DOCUMENTS.PAGE_HEADING}
                stickyColumns={[MY_DOCUMENTS_COLUMNS[0]?.name]}
                isLoading={isFetching}
            />
        </>
    );
};
